/* eslint-disable @typescript-eslint/no-unused-vars */
import * as React from 'react';
// import { useUser, useClerk, UserProfile } from '@clerk/clerk-react'; // Removed Clerk imports
import { DarkModeToggle } from '@/components/DarkModeToggle';
import { LogoutIcon } from '@/components/ui/icons/logout-icon';
import { ConnectIcon } from '@/components/ui/icons/connect-icon';
import { UsersIcon } from '@/components/ui/icons/users-icon';
import { FlameIcon } from '@/components/ui/icons/flame-icon';
import { ShareIcon } from '@/components/ui/icons/share-icon';
import { ChartColumnIcon } from '@/components/ui/icons/chart-column-icon';
import { PersonIcon } from '@/components/ui/icons/person-icon';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';
import { useTheme } from 'next-themes';
import { useNavigate, useLocation } from 'react-router-dom';
import { DarkModeIcon } from '@/components/ui/icons/dark-mode-icon';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { CogIcon } from '@/components/ui/icons/cog-icon';
import { PageLoader } from '@/components/ui/loader';
import { createRoot } from 'react-dom/client';
import { LinkIcon } from '@/components/ui/icons/link-icon';
import { useAuth } from '@/hooks/useAuth'; // Our hook
import { useWeb3Auth } from '@/providers/Web3AuthProvider'; // Web3Auth hook
import { ChartPieIcon } from '@/components/ui/icons/chart-pie-icon';
import { ArchiveIcon } from '@/components/ui/icons/archive-icon';
import { KeyIcon } from '@/components/ui/icons/key-icon';
import { ClapIcon } from '@/components/ui/icons/clap-icon';

export function UserMenu() {
	// const { user: clerkUser, isSignedIn: isClerkSignedIn } = useUser(); // Remove Clerk
	const { user, isAdmin, isSuperAdmin, isLoading } = useAuth(); // Use our hook
	const { logout, user: web3AuthUser, walletAddress } = useWeb3Auth(); // Use Web3Auth hook for logout and user info
	const isSignedIn = Boolean(user) && !isLoading; // Derive from our hook

	// const { signOut: clerkSignOut } = useClerk(); // Remove Clerk
	const { toast } = useToast();
	const { theme, setTheme } = useTheme();
	const navigate = useNavigate();
	const location = useLocation();
	const [isOpen, setIsOpen] = React.useState(false);
	const [showSignOutDialog, setShowSignOutDialog] = React.useState(false);
	const [isHovered, setIsHovered] = React.useState(false);
	const [isAvatarHovered, setIsAvatarHovered] = React.useState(false);
	const [isApiKeysHovered, setIsApiKeysHovered] = React.useState(false);
	const [isUsersHovered, setIsUsersHovered] = React.useState(false);
	const [isAiToolsHovered, setIsAiToolsHovered] = React.useState(false);
	const [isDarkModeHovered, setIsDarkModeHovered] = React.useState(false);
	const [isCogHovered, setIsCogHovered] = React.useState(false);
	const [isLinksHovered, setIsLinksHovered] = React.useState(false);
	const [isProfilesHovered, setIsProfilesHovered] = React.useState(false);
	const [showUserProfile, setShowUserProfile] = React.useState(false);
	const [showSignOutLoader, setShowSignOutLoader] = React.useState(false);
	const menuRef = React.useRef<HTMLDivElement>(null);
	const modalRef = React.useRef<HTMLDivElement>(null);
	const [isOracleHovered, setIsOracleHovered] = React.useState(false);
	const [isAnalyticsHovered, setIsAnalyticsHovered] = React.useState(false);
	const [isArchiveHovered, setIsArchiveHovered] = React.useState(false);
	const [isKeysHovered, setIsKeysHovered] = React.useState(false);
	const [isVideoEngineHovered, setIsVideoEngineHovered] =
		React.useState(false);

	const toggleTheme = () => {
		setTheme(theme === 'dark' ? 'light' : 'dark');
	};

	const handleSignOut = async () => {
		try {
			setIsOpen(false);
			setShowSignOutLoader(true); // Show loading state
			console.log('UserMenu: Initiating logout process');

			// Clear session storage login flag
			sessionStorage.removeItem('loginProcessedForThisSession');

			// Short delay to ensure UI updates before redirect
			setTimeout(async () => {
				try {
					await logout(); // Use Web3Auth logout - this has been updated to handle full cleanup

					// Force navigation to home page
					window.location.href = '/';
				} catch (error) {
					console.error('UserMenu: Logout error:', error);
					// Force a page reload as last resort
					window.location.href = '/';
				}
			}, 200); // Increased delay to ensure state updates
		} catch (error) {
			console.error('UserMenu: Error in logout handler:', error);
			toast({
				title: 'Error',
				description: 'Failed to sign out. Please try again.',
				variant: 'destructive',
				duration: 3000,
			});
			setShowSignOutLoader(false);

			// Even on error, attempt to navigate home
			window.location.href = '/';
		}
	};

	// Close menu when clicking outside
	React.useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (
				menuRef.current &&
				!menuRef.current.contains(event.target as Node)
			) {
				setIsOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () =>
			document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	// Add click outside handler for the modal
	const handleModalClick = (event: React.MouseEvent<HTMLDivElement>) => {
		if (event.target === event.currentTarget) {
			setShowUserProfile(false);
		}
	};

	// Don't render the menu on the home route or if user is not signed in or still loading
	if (location.pathname === '/' || isLoading || !isSignedIn || !user) {
		return null;
	}

	// Removed UserProfile logic for now, account management needs a replacement
	// const menuItems definition remains largely the same, but remove the Account item for now
	const baseMenuItems = [
		{
			label: 'Wallet',
			icon: (isHovered: boolean) => <CogIcon isHovered={isHovered} />,
			onClick: () => {
				navigate('/admin/profile');
				setIsOpen(false);
			},
			onHover: setIsCogHovered,
		},
		{
			label: 'Manage Links',
			icon: (isHovered: boolean) => <LinkIcon isHovered={isHovered} />,
			onClick: () => {
				navigate(isAdmin ? '/admin/urls' : '/user/urls');
				setIsOpen(false);
			},
			onHover: setIsLinksHovered,
		},
		{
			label: 'API Keys',
			icon: (isHovered: boolean) => <KeyIcon isHovered={isHovered} />,
			onClick: () => {
				navigate('/admin/keys');
				setIsOpen(false);
			},
			onHover: setIsKeysHovered,
		},
		// SuperAdmin Items
		...(isSuperAdmin
			? [
					{
						label: 'AI Tools',
						icon: (isHovered: boolean) => (
							<FlameIcon isHovered={isHovered} />
						),
						onClick: () => {
							navigate('/admin/ai');
							setIsOpen(false);
						},
						onHover: setIsAiToolsHovered,
					},
					{
						label: 'Users',
						icon: (isHovered: boolean) => (
							<UsersIcon isHovered={isHovered} />
						),
						onClick: () => {
							navigate('/admin/users');
							setIsOpen(false);
						},
						onHover: setIsUsersHovered,
					},
				]
			: []),

		{
			label: 'The Oracle',
			icon: (isHovered: boolean) => (
				<ChartColumnIcon isHovered={isHovered} />
			),
			onClick: () => {
				// Navigate based on admin status
				navigate(isAdmin ? '/admin/oracle' : '/user/oracle');
				setIsOpen(false);
			},
			onHover: setIsOracleHovered,
		},
		{
			label: 'Analytics',
			icon: (isHovered: boolean) => (
				<ChartPieIcon isHovered={isHovered} />
			),
			onClick: () => {
				// Navigate based on admin status
				navigate(isAdmin ? '/admin/analytics' : '/user/analytics');
				setIsOpen(false);
			},
			onHover: setIsAnalyticsHovered,
		},
		{
			label: 'Archive',
			icon: (isHovered: boolean) => (
				<ArchiveIcon isHovered={isHovered} />
			),
			onClick: () => {
				// Navigate to archive page
				navigate('/admin/content');
				setIsOpen(false);
			},
			onHover: setIsArchiveHovered,
		},
		{
			label: 'Video Engine',
			icon: (isHovered: boolean) => <ClapIcon isHovered={isHovered} />,
			onClick: () => {
				navigate('/admin/video');
				setIsOpen(false);
			},
			onHover: setIsVideoEngineHovered,
		},
	];

	// Combine items based on admin status (if needed, seems handled above now)
	const menuItems = baseMenuItems;

	return (
		<div className='fixed top-5 right-5 z-50' ref={menuRef}>
			<button
				onClick={() => setIsOpen(!isOpen)}
				onMouseEnter={() => setIsAvatarHovered(true)}
				onMouseLeave={() => setIsAvatarHovered(false)}
				className='w-14 h-14 rounded-full overflow-hidden border-2 border-pink-800/50 hover:border-pink-400 transition-all flex items-center justify-center bg-gradient-to-b from-zinc-900 via-purple-900/40 to-black shadow-[0_0_15px_rgba(236,72,153,0.5)]'
			>
				<div className='scale-150'>
					<PersonIcon isHovered={isAvatarHovered || isOpen} />
				</div>
			</button>

			{isOpen && (
				<div className='absolute right-0 mt-2 w-48 rounded-md shadow-lg bg-background border border-border'>
					<div className='py-1' role='menu'>
						{/* Display user email or truncated wallet address */}
						{/* <div className='px-4 py-2 text-sm text-foreground border-b border-border'>
							{user?.email ||
								(walletAddress
									? `${walletAddress.substring(
											0,
											6
										)}...${walletAddress.substring(
											walletAddress.length - 4
										)}`
									: 'User')}
						</div> */}

						{menuItems.map((item, index) => (
							<button
								key={item.label}
								onClick={item.onClick}
								onMouseEnter={() => item.onHover(true)}
								onMouseLeave={() => item.onHover(false)}
								className='w-full flex items-center px-4 py-2 text-sm font-medium transition-colors hover:bg-muted border-b border-border'
								role='menuitem'
							>
								{item.icon(
									item.label === 'Wallet'
										? isCogHovered
										: item.label === 'Analytics'
											? isAnalyticsHovered
											: item.label === 'Archive'
												? isArchiveHovered
												: item.label === 'The Oracle'
													? isOracleHovered
													: item.label === 'Manage Links'
														? isLinksHovered
														: item.label ===
															  'Social Profiles'
															? isProfilesHovered
															: item.label ===
																  'AI Tools'
																? isAiToolsHovered
																: item.label ===
																	  'Users'
																	? isUsersHovered
																	: item.label ===
																		  'API Keys'
																		? isKeysHovered
																		: item.label ===
																			  'Video Engine'
																			? isVideoEngineHovered
																			: false
								)}
								<span className='ml-3'>{item.label}</span>
							</button>
						))}

						<button
							onClick={toggleTheme}
							onMouseEnter={() => setIsDarkModeHovered(true)}
							onMouseLeave={() => setIsDarkModeHovered(false)}
							className='w-full flex items-center px-4 py-2 text-sm font-medium transition-colors hover:bg-muted border-b border-gray-200 dark:border-gray-700'
						>
							<DarkModeIcon isHovered={isDarkModeHovered} />
							<span className='ml-3'>Dark Mode</span>
						</button>

						<button
							onClick={() => setShowSignOutDialog(true)}
							onMouseEnter={() => setIsHovered(true)}
							onMouseLeave={() => setIsHovered(false)}
							className={cn(
								'w-full flex items-center px-4 py-2 text-sm font-medium transition-colors',
								'hover:bg-muted text-pink-600'
							)}
							role='menuitem'
						>
							<LogoutIcon isHovered={isHovered} />
							<span className='ml-3'>Sign Out</span>
						</button>
					</div>
				</div>
			)}

			<ConfirmDialog
				isOpen={showSignOutDialog}
				onClose={() => setShowSignOutDialog(false)}
				onConfirm={() => {
					setShowSignOutDialog(false);
					setIsOpen(false);
					void handleSignOut();
				}}
				title='Sign Out'
				description='Are you sure you want to sign out?'
				confirmText='Yes, Sign Out'
				cancelText='Cancel'
			/>
		</div>
	);
}
